/**
 * Business Line Service
 *
 * Reads the CMS-managed Business Lines + Sub Types tree that drives
 * the WO / Inspection / Location pickers (and admin tab CRUD).
 *
 * Backend endpoints:
 *   GET    /api/business-lines                       — list (with sub-types)
 *   POST   /api/business-lines                       — create line (super_admin)
 *   PATCH  /api/business-lines/:id                   — update line
 *   DELETE /api/business-lines/:id                   — delete line (non-system only)
 *   POST   /api/business-lines/:id/sub-types         — create sub-type
 *   PATCH  /api/business-line-sub-types/:id          — update sub-type
 *   DELETE /api/business-line-sub-types/:id          — delete sub-type (non-system only)
 *
 * The frontend stores the slug — not the id — on WO/Inspection records.
 * Slugs are the stable identifier business rules branch on (e.g.
 * "construction → signature required") and they survive renames.
 */

import { api } from './api';

/**
 * One row from the business_line_sub_types table. Read-only fields
 * (id, created/updated timestamps) are omitted from the create/update
 * payloads below.
 */
export interface BusinessLineSubType {
	id: number;
	business_line_id: number;
	slug: string;
	label: string;
	is_active: boolean;
	is_system: boolean;
	sort_order: number;
	default_checklist_id: number | null;
}

export interface BusinessLine {
	id: number;
	slug: string;
	label: string;
	is_active: boolean;
	is_system: boolean;
	sort_order: number;
	default_checklist_id: number | null;
	/** Eager-loaded children, ordered by sort_order. Always present on
	 *  the list endpoint; might be absent on detail responses. */
	sub_types?: BusinessLineSubType[];
}

/** Create payload for a new business line. Slug auto-derives from
 *  the label server-side when omitted. */
export interface CreateBusinessLineData {
	slug?: string;
	label: string;
	is_active?: boolean;
	sort_order?: number;
}

/** Partial update — all fields optional. */
export type UpdateBusinessLineData = Partial<CreateBusinessLineData> & {
	default_checklist_id?: number | null;
};

export interface CreateBusinessLineSubTypeData {
	slug?: string;
	label: string;
	is_active?: boolean;
	sort_order?: number;
}

export type UpdateBusinessLineSubTypeData = Partial<CreateBusinessLineSubTypeData> & {
	default_checklist_id?: number | null;
};

export const businessLineService = {
	/**
	 * List every line (with sub-types). Pass active=true to filter
	 * to only the user-pickable rows — used by the WO/Inspection forms.
	 * The CMS admin tab fetches without the filter so disabled rows
	 * can be re-enabled.
	 */
	list: (activeOnly = false) =>
		api.get<{ data: BusinessLine[] }>(
			'/business-lines',
			activeOnly ? { active: '1' } : undefined,
		),

	create: (data: CreateBusinessLineData) =>
		api.post<{ data: BusinessLine }>('/business-lines', data),

	update: (id: number | string, data: UpdateBusinessLineData) =>
		api.patch<{ data: BusinessLine }>(`/business-lines/${id}`, data),

	delete: (id: number | string) =>
		api.delete<void>(`/business-lines/${id}`),

	createSubType: (businessLineId: number | string, data: CreateBusinessLineSubTypeData) =>
		api.post<{ data: BusinessLineSubType }>(
			`/business-lines/${businessLineId}/sub-types`,
			data,
		),

	updateSubType: (subTypeId: number | string, data: UpdateBusinessLineSubTypeData) =>
		api.patch<{ data: BusinessLineSubType }>(
			`/business-line-sub-types/${subTypeId}`,
			data,
		),

	deleteSubType: (subTypeId: number | string) =>
		api.delete<void>(`/business-line-sub-types/${subTypeId}`),
};
